/**
 * First-party, privacy-friendly page-view beacon. Sends an anonymous hit to
 * the backend on each navigation so the admin dashboard can show unique vs
 * returning visitor counts. No PII, no third party, no cross-site tracking —
 * just a random per-browser id in localStorage.
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? '/api/v1').toString();
const VID_KEY = 'bq_vid';
const SID_KEY = 'bq_sid';

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function persistentId(store: Storage, key: string): string {
  let v = store.getItem(key);
  if (!v) {
    v = randomId();
    store.setItem(key, v);
  }
  return v;
}

/** Fire a page view. Never throws — analytics must not break the app. */
export function trackPageView(path: string, lang: string): void {
  try {
    // Skip bots, Playwright/prerender (navigator.webdriver), and admin usage.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;
    if (path.startsWith('/adminpanel')) return;

    const body = JSON.stringify({
      visitorId: persistentId(localStorage, VID_KEY),
      sessionId: persistentId(sessionStorage, SID_KEY),
      path,
      referrer: document.referrer || null,
      lang,
      device: window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop',
    });

    void fetch(`${API_BASE}/analytics/hit`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* fire-and-forget */
    });
  } catch {
    /* storage blocked / private mode — silently skip */
  }
}
